import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { 
  ArrowLeft, 
  FloppyDisk, 
  CheckCircle,
  Clock,
  Archive,
  ClockCounterClockwise
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultFormData = {
  title: "",
  category: "",
  answer_markdown: "",
  site_scope: ["*"],
  product_tags: [],
  sources: [],
  status: "draft",
  safety_level: "normal"
};

const categoryOptions = [
  "Levering",
  "Returnering",
  "Produkter",
  "Priser",
  "Måling",
  "Installation",
  "Betaling",
  "Kontakt",
  "Generelt"
];

export const ArticleEditorPage = ({ user, setUser }) => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const isNew = !articleId || articleId === 'new';

  const [formData, setFormData] = useState(defaultFormData);
  const [sites, setSites] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    fetchSites();
    if (!isNew) {
      fetchArticle();
      fetchVersions();
    }
  }, [articleId]);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchArticle = async () => {
    try {
      const response = await fetch(`${API}/knowledge-articles/${articleId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title || "",
          category: data.category || "",
          answer_markdown: data.answer_markdown || "",
          site_scope: data.site_scope || ["*"],
          product_tags: data.product_tags || [],
          sources: data.sources || [],
          status: data.status || "draft",
          safety_level: data.safety_level || "normal"
        });
        setTagsInput(data.product_tags?.join(", ") || "");
      } else {
        toast.error("Artikel ikke fundet");
        navigate('/knowledge-base');
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      toast.error("Kunne ikke hente artikel");
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await fetch(`${API}/knowledge-articles/${articleId}/versions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  const handleRestoreVersion = (version) => {
    // Restore the version by updating form data
    setFormData({
      ...formData,
      title: version.title,
      answer_markdown: version.answer_markdown,
      category: version.category,
      status: version.status
    });
    toast.success(`Version ${version.version} indlæst. Klik "Gem" for at gendanne.`);
    // Switch to edit tab
    document.querySelector('[data-testid="tab-edit"]')?.click();
  };

  const handleSave = async () => {
    if (!formData.title || !formData.category || !formData.answer_markdown) {
      toast.error("Udfyld venligst titel, kategori og svar");
      return;
    }

    setSaving(true);
    try {
      // Parse tags from input
      const tags = tagsInput
        .split(",")
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const payload = {
        ...formData,
        product_tags: tags
      };

      const url = isNew 
        ? `${API}/knowledge-articles`
        : `${API}/knowledge-articles/${articleId}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(isNew ? "Artikel oprettet" : "Artikel opdateret");
        if (isNew) {
          navigate(`/knowledge-base/${data.article_id}`);
        } else {
          fetchVersions();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke gemme artikel");
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error("Der opstod en fejl");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    try {
      const response = await fetch(`${API}/knowledge-articles/${articleId}/verify`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Artikel verificeret");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke verificere artikel");
      }
    } catch (error) {
      console.error('Verify error:', error);
      toast.error("Der opstod en fejl");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "d. MMM yyyy HH:mm", { locale: da });
    } catch {
      return "-";
    }
  };

  const StatusIcon = ({ status }) => {
    const icons = {
      approved: CheckCircle,
      draft: Clock,
      retired: Archive
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-4 h-4" weight="bold" />;
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="p-6 md:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3" />
            <div className="h-64 bg-slate-100 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="article-editor-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/knowledge-base')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Tilbage
            </Button>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {isNew ? "Ny artikel" : "Rediger artikel"}
            </h1>
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <Button 
                variant="outline"
                onClick={handleVerify}
                data-testid="verify-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Verificer
              </Button>
            )}
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              data-testid="save-btn"
            >
              <FloppyDisk className="w-4 h-4 mr-2" />
              {saving ? "Gemmer..." : "Gem"}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="edit" className="space-y-6">
          <TabsList>
            <TabsTrigger value="edit" data-testid="tab-edit">Rediger</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">Forhåndsvisning</TabsTrigger>
            {!isNew && (
              <TabsTrigger value="history" data-testid="tab-history">
                <ClockCounterClockwise className="w-4 h-4 mr-1" />
                Historik
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="edit">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="card-default">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Titel *</Label>
                      <Input
                        id="title"
                        placeholder="F.eks. Hvordan returnerer jeg min ordre?"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        data-testid="title-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="answer">Svar (Markdown) *</Label>
                      <Textarea
                        id="answer"
                        placeholder="Skriv svaret her. Du kan bruge Markdown formattering..."
                        value={formData.answer_markdown}
                        onChange={(e) => setFormData({...formData, answer_markdown: e.target.value})}
                        className="min-h-[300px] font-mono text-sm"
                        data-testid="answer-input"
                      />
                      <p className="text-xs text-slate-500">
                        Understøtter Markdown: **fed**, *kursiv*, [link](url), lister, osv.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-default">
                  <CardHeader>
                    <CardTitle className="text-base">Produkt tags</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Input
                      placeholder="rullegardiner, plissegardiner, zebragardiner (kommasepareret)"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      data-testid="tags-input"
                    />
                    <p className="text-xs text-slate-500">
                      Tags hjælper chatbotten med at finde relevante artikler baseret på produkter
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <Card className="card-default">
                  <CardHeader>
                    <CardTitle className="text-base">Indstillinger</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Kategori *</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(val) => setFormData({...formData, category: val})}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Vælg kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryOptions.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={formData.status} 
                        onValueChange={(val) => setFormData({...formData, status: val})}
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-500" />
                              Kladde
                            </div>
                          </SelectItem>
                          <SelectItem value="approved">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              Godkendt
                            </div>
                          </SelectItem>
                          <SelectItem value="retired">
                            <div className="flex items-center gap-2">
                              <Archive className="w-4 h-4 text-amber-500" />
                              Arkiveret
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Kun godkendte artikler bruges af chatbotten
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Sikkerhedsniveau</Label>
                      <Select 
                        value={formData.safety_level} 
                        onValueChange={(val) => setFormData({...formData, safety_level: val})}
                      >
                        <SelectTrigger data-testid="safety-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="sensitive">Sensitiv</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-default">
                  <CardHeader>
                    <CardTitle className="text-base">Site scope</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Select 
                      value={formData.site_scope.includes("*") ? "*" : formData.site_scope[0] || "*"} 
                      onValueChange={(val) => setFormData({
                        ...formData, 
                        site_scope: val === "*" ? ["*"] : [val]
                      })}
                    >
                      <SelectTrigger data-testid="site-scope-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="*">Alle sites</SelectItem>
                        {sites.map(site => (
                          <SelectItem key={site.site_key} value={site.site_key}>
                            {site.brand_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Vælg hvilke sites denne artikel gælder for
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preview">
            <Card className="card-default">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">
                  {formData.title || "Ingen titel"}
                </h2>
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline">{formData.category || "Ingen kategori"}</Badge>
                  <Badge variant={formData.status === "approved" ? "default" : "secondary"}>
                    <StatusIcon status={formData.status} />
                    <span className="ml-1">
                      {formData.status === "approved" ? "Godkendt" : 
                       formData.status === "retired" ? "Arkiveret" : "Kladde"}
                    </span>
                  </Badge>
                </div>
                <div className="markdown-content prose prose-slate max-w-none">
                  {formData.answer_markdown ? (
                    <div dangerouslySetInnerHTML={{ 
                      __html: formData.answer_markdown
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/\n/g, '<br/>')
                    }} />
                  ) : (
                    <p className="text-slate-400 italic">Intet indhold endnu</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {!isNew && (
            <TabsContent value="history">
              <Card className="card-default">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClockCounterClockwise className="w-5 h-5 text-indigo-600" weight="duotone" />
                    Versionshistorik
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {versions.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">Ingen versionshistorik endnu</p>
                  ) : (
                    <div className="space-y-3">
                      {versions.map((version, index) => (
                        <motion.div
                          key={version.version_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`border rounded-lg overflow-hidden ${index === 0 ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-200'}`}
                        >
                          <div 
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                            onClick={() => setExpandedVersion(expandedVersion === version.version_id ? null : version.version_id)}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                                <span className={`text-sm font-semibold ${index === 0 ? 'text-indigo-600' : 'text-slate-600'}`}>v{version.version}</span>
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-medium text-slate-900">{version.title}</span>
                                  {index === 0 && (
                                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">Nuværende</Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {version.status === 'approved' ? 'Godkendt' : version.status === 'draft' ? 'Kladde' : 'Arkiveret'}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-500">
                                  {version.change_note || "Ændret"} • {formatDate(version.changed_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {index > 0 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRestoreVersion(version);
                                  }}
                                  data-testid={`restore-version-${version.version}`}
                                >
                                  <ClockCounterClockwise className="w-4 h-4 mr-1" />
                                  Gendan
                                </Button>
                              )}
                              <svg 
                                className={`w-5 h-5 text-slate-400 transition-transform ${expandedVersion === version.version_id ? 'rotate-180' : ''}`}
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                          
                          {expandedVersion === version.version_id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-slate-200 bg-slate-50/50 p-4"
                            >
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs text-slate-500">Kategori</Label>
                                  <p className="text-sm font-medium">{version.category}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-500">Indhold</Label>
                                  <div className="mt-1 p-3 bg-white rounded border border-slate-200 text-sm font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">
                                    {version.answer_markdown}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};
